
import React from 'react';
import { Upload, FileText, CheckCircle, X, File, Package, Ship } from 'lucide-react';
import ProcessingPipeline from '../components/ProcessingPipeline';
import type { UploadedFile } from '../types';

interface UploadViewProps {
  uploadedFiles: UploadedFile[];
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLElement>) => void;
  removeFile: (fileName: string) => void;
  isProcessing: boolean;
  processDocuments: () => void;
  processingStage: number;
}

const UploadView: React.FC<UploadViewProps> = ({ uploadedFiles, handleFileUpload, removeFile, isProcessing, processDocuments, processingStage }) => {
  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="text-3xl font-bold text-white mb-2">New Declaration</h2>
        <p className="text-gray-400">Upload your shipping documents to begin</p>
      </div>

      <div className="glass-card rounded-xl p-8">
        <label
          htmlFor="file-upload"
          className="border-2 border-dashed border-gray-600 rounded-xl p-12 flex flex-col items-center justify-center cursor-pointer hover:border-teal-500 hover:bg-gray-800 transition-all group"
          onDrop={(e) => {
            e.preventDefault();
            handleFileUpload(e);
          }}
          onDragOver={(e) => e.preventDefault()}
        >
          <div className="p-4 bg-gray-700 group-hover:bg-teal-900/50 rounded-full mb-4 transition-colors">
            <Upload className="text-gray-400 group-hover:text-teal-400" size={48} />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Drop files, click to browse, or take a picture</h3>
          <p className="text-gray-400 mb-4">Support for PDF, JPEG, PNG, DOCX, XLSX files</p>
          <div className="flex gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-2"><File size={16} /> Invoices</span>
            <span className="flex items-center gap-2"><Package size={16} /> Packing Lists</span>
            <span className="flex items-center gap-2"><Ship size={16} /> Bill of Lading</span>
          </div>
          <input
            id="file-upload"
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx,image/*"
            capture="environment"
            className="hidden"
            onChange={handleFileUpload}
          />
        </label>
      </div>

      {uploadedFiles.length > 0 && (
        <div className="glass-card rounded-xl p-6 animate-slideUp">
          <h3 className="text-xl font-bold text-white mb-4">Uploaded Documents ({uploadedFiles.length})</h3>
          <div className="space-y-3 mb-6">
            {uploadedFiles.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-800 rounded-lg border border-gray-700">
                <div className="flex items-center gap-4 overflow-hidden">
                  <div className="p-2 bg-blue-900/50 rounded-lg flex-shrink-0">
                    <FileText className="text-blue-400" size={20} />
                  </div>
                  <div className="truncate">
                    <p className="font-semibold text-white truncate">{file.name}</p>
                    <p className="text-sm text-gray-400">{(file.size / 1024).toFixed(2)} KB</p>
                  </div>
                </div>
                <button onClick={() => removeFile(file.name)} className="p-2 hover:bg-red-900/50 text-red-500 rounded-lg transition-colors flex-shrink-0 ml-4">
                  <X size={20} />
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={processDocuments}
            disabled={isProcessing}
            className="w-full bg-gradient-to-r from-teal-600 to-blue-700 text-white px-6 py-4 rounded-lg font-semibold hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isProcessing ? (
              <span className="animate-pulse-strong">Processing Documents...</span>
            ) : (
              <>
                <CheckCircle size={20} />
                Process Documents
              </>
            )}
          </button>
        </div>
      )}

      {isProcessing && <ProcessingPipeline processingStage={processingStage} />}
    </div>
  );
};

export default UploadView;